import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { usePositionTrades } from '@hub/features/account-dashboard/hooks';
import { Alert, CircularProgress } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Modal } from '@monorepo/nova-ui';
import type { ClosedPosition, OpenPosition } from '@monorepo/types';
import { TradesTable } from './components';
import {
  StyledCloseButton,
  StyledCloseButtonBottom,
  StyledFooter,
  StyledHeader,
  StyledLoadingContainer,
  StyledModalContent,
  StyledTitle,
} from './styled';

interface PositionDetailsModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  position: OpenPosition | ClosedPosition | null;
  tsAccountId: string;
}

export const PositionDetailsModal: FC<PositionDetailsModalProps> = ({
  isModalOpen,
  onClose,
  position,
  tsAccountId,
}) => {
  const { t } = useTranslation(['position', 'common']);

  const positionId = position?.id;
  const enabled = isModalOpen && !!position && !!positionId && !!tsAccountId;

  const {
    data: tradesData,
    isLoading: isLoadingTrades,
    error: tradesError,
  } = usePositionTrades({
    tsAccountId,
    positionId,
    enabled,
  });

  const positionState = position && 'closeDate' in position ? 'closed' : 'open';

  const trades = useMemo(() => {
    if (!tradesData?.results) return [];
    return sortBy(tradesData.results, 'date');
  }, [tradesData]);

  const modalTitle = t('position:positions.position_details_for', {
    symbol: position?.symbol || '',
  });

  const isDataReady = !isLoadingTrades && !tradesError && tradesData;

  return (
    <Modal open={isModalOpen} onClose={onClose}>
      <StyledModalContent spacing={3}>
        <StyledHeader>
          <StyledTitle>{modalTitle}</StyledTitle>
          <StyledCloseButton onClick={onClose} aria-label={t('common:close')}>
            <SystemIcon iconName="X" size="small" color="primary" />
          </StyledCloseButton>
        </StyledHeader>

        {isLoadingTrades && (
          <StyledLoadingContainer>
            <CircularProgress />
          </StyledLoadingContainer>
        )}

        {tradesError && (
          <Alert severity="error">{t('common:error.failed_to_load')}</Alert>
        )}

        {isDataReady && (
          <TradesTable trades={trades} positionState={positionState} />
        )}

        <StyledFooter>
          <StyledCloseButtonBottom variant="outlined" onClick={onClose}>
            {t('common:close')}
          </StyledCloseButtonBottom>
        </StyledFooter>
      </StyledModalContent>
    </Modal>
  );
};
